import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { format } from 'date-fns';

import { reportsApi, companiesApi } from '../../services/api';

interface DashboardStats {
  totalDevices: number;
  activeDevices: number;
  offlineDevices: number;
  overdueDevices: number;
  repoDevices: number;
  totalAlerts: number;
  unreadAlerts: number;
}

interface OfflineDevice {
  id: string;
  imei: string;
  name: string | null;
  lastOfflineAt: string | null;
  company: {
    name: string;
    code: string;
  };
}

interface OverdueDevice {
  id: string;
  imei: string;
  name: string | null;
  isAggressive: boolean;
  client: {
    name: string;
    phone: string | null;
  } | null;
  company: {
    name: string;
    code: string;
  };
}

interface Company {
  id: string;
  name: string;
  code: string;
}

type ReportType = 'dashboard' | 'offline' | 'overdue' | 'repo';

export function ReportsPage() {
  const [selectedReport, setSelectedReport] = useState<ReportType>('dashboard');
  const [selectedCompany, setSelectedCompany] = useState<string>('');

  const { data: companiesData } = useQuery({
    queryKey: ['companies'],
    queryFn: async () => {
      const response = await companiesApi.list({ limit: 100 });
      return response.data;
    },
  });

  const { data: dashboardData, isLoading: isDashboardLoading } = useQuery({
    queryKey: ['reports', 'dashboard', selectedCompany],
    queryFn: async () => {
      const params: Record<string, unknown> = {};
      if (selectedCompany) params.companyId = selectedCompany;
      const response = await reportsApi.getDashboard(params);
      return response.data;
    },
    enabled: selectedReport === 'dashboard',
  });

  const { data: offlineData, isLoading: isOfflineLoading } = useQuery({
    queryKey: ['reports', 'offline', selectedCompany],
    queryFn: async () => {
      const params: Record<string, unknown> = { limit: 100 };
      if (selectedCompany) params.companyId = selectedCompany;
      const response = await reportsApi.getOfflineDevices(params);
      return response.data;
    },
    enabled: selectedReport === 'offline',
  });

  const { data: overdueData, isLoading: isOverdueLoading } = useQuery({
    queryKey: ['reports', 'overdue', selectedCompany],
    queryFn: async () => {
      const params: Record<string, unknown> = { limit: 100 };
      if (selectedCompany) params.companyId = selectedCompany;
      const response = await reportsApi.getOverdueDevices(params);
      return response.data;
    },
    enabled: selectedReport === 'overdue',
  });

  const { data: repoData, isLoading: isRepoLoading } = useQuery({
    queryKey: ['reports', 'repo', selectedCompany],
    queryFn: async () => {
      const params: Record<string, unknown> = { limit: 100 };
      if (selectedCompany) params.companyId = selectedCompany;
      const response = await reportsApi.getRepoDevices(params);
      return response.data;
    },
    enabled: selectedReport === 'repo',
  });

  const companies = (companiesData?.data || []) as Company[];
  // The API returns dashboard stats grouped (devices/alerts/...); map to the flat shape the UI renders.
  const rawStats = dashboardData?.data as
    | {
        devices: { total: number; active: number; offline: number; overdue: number; inRepo: number };
        alerts: { total: number; unread: number };
      }
    | undefined;
  const stats: DashboardStats | undefined = rawStats
    ? {
        totalDevices: rawStats.devices.total,
        activeDevices: rawStats.devices.active,
        offlineDevices: rawStats.devices.offline,
        overdueDevices: rawStats.devices.overdue,
        repoDevices: rawStats.devices.inRepo,
        totalAlerts: rawStats.alerts.total,
        unreadAlerts: rawStats.alerts.unread,
      }
    : undefined;
  const offlineDevices = (offlineData?.data || []) as OfflineDevice[];
  const overdueDevices = (overdueData?.data || []) as OverdueDevice[];
  const repoDevices = (repoData?.data || []) as OverdueDevice[];

  const isLoading =
    (selectedReport === 'dashboard' && isDashboardLoading) ||
    (selectedReport === 'offline' && isOfflineLoading) ||
    (selectedReport === 'overdue' && isOverdueLoading) ||
    (selectedReport === 'repo' && isRepoLoading);

  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Reportes</h1>
        <p className="text-gray-600">Estadísticas y reportes del sistema</p>
      </div>

      {/* Report Selector */}
      <div className="bg-white shadow rounded-lg p-4 mb-6">
        <div className="flex flex-wrap gap-4">
          <div>
            <label htmlFor="reports-type" className="block text-sm font-medium text-gray-700 mb-1">
              Tipo de Reporte
            </label>
            <select
              id="reports-type"
              value={selectedReport}
              onChange={(e) => setSelectedReport(e.target.value as ReportType)}
              className="w-48 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="dashboard">Dashboard General</option>
              <option value="offline">Dispositivos Offline</option>
              <option value="overdue">Préstamos Atrasados</option>
              <option value="repo">En Proceso de Incaute</option>
            </select>
          </div>
          <div>
            <label htmlFor="reports-company" className="block text-sm font-medium text-gray-700 mb-1">
              Empresa
            </label>
            <select
              id="reports-company"
              value={selectedCompany}
              onChange={(e) => setSelectedCompany(e.target.value)}
              className="w-48 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Todas</option>
              {companies.map((company) => (
                <option key={company.id} value={company.id}>
                  {company.name}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {isLoading ? (
        <div className="text-center py-10">Cargando...</div>
      ) : (
        <>
          {/* Dashboard Stats */}
          {selectedReport === 'dashboard' && stats && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <div className="bg-white shadow rounded-lg p-6">
                <div className="text-sm font-medium text-gray-500 mb-1">
                  Total Dispositivos
                </div>
                <div className="text-3xl font-bold text-gray-900">
                  {stats.totalDevices}
                </div>
                <div className="text-sm text-gray-500 mt-2">
                  {stats.activeDevices} activos
                </div>
              </div>
              <div className="bg-white shadow rounded-lg p-6">
                <div className="text-sm font-medium text-gray-500 mb-1">
                  Dispositivos Offline
                </div>
                <div className="text-3xl font-bold text-red-600">
                  {stats.offlineDevices}
                </div>
                <div className="text-sm text-gray-500 mt-2">
                  Sin conexion reciente
                </div>
              </div>
              <div className="bg-white shadow rounded-lg p-6">
                <div className="text-sm font-medium text-gray-500 mb-1">
                  Préstamos Atrasados
                </div>
                <div className="text-3xl font-bold text-orange-600">
                  {stats.overdueDevices}
                </div>
                <div className="text-sm text-gray-500 mt-2">
                  Requieren atencion
                </div>
              </div>
              <div className="bg-white shadow rounded-lg p-6">
                <div className="text-sm font-medium text-gray-500 mb-1">
                  En Incaute
                </div>
                <div className="text-3xl font-bold text-purple-600">
                  {stats.repoDevices}
                </div>
                <div className="text-sm text-gray-500 mt-2">
                  Proceso activo
                </div>
              </div>
              <div className="bg-white shadow rounded-lg p-6">
                <div className="text-sm font-medium text-gray-500 mb-1">
                  Total Alertas
                </div>
                <div className="text-3xl font-bold text-gray-900">
                  {stats.totalAlerts}
                </div>
                <div className="text-sm text-gray-500 mt-2">
                  {stats.unreadAlerts} sin leer
                </div>
              </div>
            </div>
          )}

          {/* Offline Devices Report */}
          {selectedReport === 'offline' && (
            <div className="bg-white shadow rounded-lg overflow-hidden">
              <div className="p-4 border-b">
                <h3 className="font-medium">
                  Dispositivos Offline ({offlineDevices.length})
                </h3>
              </div>
              {offlineDevices.length === 0 ? (
                <div className="p-6 text-center text-gray-500">
                  No hay dispositivos offline
                </div>
              ) : (
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        Dispositivo
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        IMEI
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        Empresa
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        Último offline
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {offlineDevices.map((device) => (
                      <tr key={device.id}>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="font-medium">
                            {device.name || 'Sin nombre'}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {device.imei}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="text-sm font-mono bg-gray-100 px-2 py-1 rounded">
                            {device.company.code}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {device.lastOfflineAt
                            ? format(new Date(device.lastOfflineAt), 'dd/MM/yyyy HH:mm')
                            : '-'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          )}

          {/* Overdue Devices Report */}
          {selectedReport === 'overdue' && (
            <div className="bg-white shadow rounded-lg overflow-hidden">
              <div className="p-4 border-b">
                <h3 className="font-medium">
                  Préstamos Atrasados ({overdueDevices.length})
                </h3>
              </div>
              {overdueDevices.length === 0 ? (
                <div className="p-6 text-center text-gray-500">
                  No hay prestamos atrasados
                </div>
              ) : (
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        Dispositivo
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        Cliente
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        Empresa
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        Estado
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {overdueDevices.map((device) => (
                      <tr key={device.id} className={device.isAggressive ? 'bg-red-50' : ''}>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="font-medium">
                            {device.name || 'Sin nombre'}
                          </div>
                          <div className="text-xs text-gray-500">{device.imei}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {device.client ? (
                            <div>
                              <div className="text-sm">{device.client.name}</div>
                              <div className="text-xs text-gray-500">
                                {device.client.phone || '-'}
                              </div>
                            </div>
                          ) : (
                            <span className="text-gray-400">-</span>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="text-sm font-mono bg-gray-100 px-2 py-1 rounded">
                            {device.company.code}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {device.isAggressive && (
                            <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-red-100 text-red-800">
                              Agresivo
                            </span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          )}

          {/* Repo Devices Report */}
          {selectedReport === 'repo' && (
            <div className="bg-white shadow rounded-lg overflow-hidden">
              <div className="p-4 border-b">
                <h3 className="font-medium">
                  En Proceso de Incaute ({repoDevices.length})
                </h3>
              </div>
              {repoDevices.length === 0 ? (
                <div className="p-6 text-center text-gray-500">
                  No hay dispositivos en proceso de incaute
                </div>
              ) : (
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        Dispositivo
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        Cliente
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        Empresa
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        Estado
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {repoDevices.map((device) => (
                      <tr key={device.id} className={device.isAggressive ? 'bg-red-50' : ''}>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="font-medium">
                            {device.name || 'Sin nombre'}
                          </div>
                          <div className="text-xs text-gray-500">{device.imei}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {device.client ? (
                            <div>
                              <div className="text-sm">{device.client.name}</div>
                              <div className="text-xs text-gray-500">
                                {device.client.phone || '-'}
                              </div>
                            </div>
                          ) : (
                            <span className="text-gray-400">-</span>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="text-sm font-mono bg-gray-100 px-2 py-1 rounded">
                            {device.company.code}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-purple-100 text-purple-800">
                            En incaute
                          </span>
                          {device.isAggressive && (
                            <span className="ml-2 inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-red-100 text-red-800">
                              Agresivo
                            </span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}
